import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import { adminOrderService } from '../services'
import { UPLOAD_HISTORY_POLL_MS } from '../constants/uploadHistoryPolling'
import { formatDateTime } from '../utils/dateFormat'
import useAuthStore from '../store/authStore'
import { canExportData } from '../utils/adminPermissions'

const STATUS_STYLES = {
  queued: { bgcolor: '#e3f2fd', color: '#1565c0', label: 'Queued' },
  processing: { bgcolor: '#fff3e0', color: '#e65100', label: 'Preparing' },
  preparing: { bgcolor: '#fff3e0', color: '#e65100', label: 'Preparing' },
  completed: { bgcolor: '#e8f5e9', color: '#1b5e20', label: 'Ready' },
  ready: { bgcolor: '#e8f5e9', color: '#1b5e20', label: 'Ready' },
  failed: { bgcolor: '#ffebee', color: '#c62828', label: 'Failed' },
  no_data: { bgcolor: '#eceff1', color: '#455a64', label: 'No data' },
  expired: { bgcolor: '#eceff1', color: '#546e7a', label: 'Expired' },
}

function scopeSummary(job) {
  return job.search || '—'
}

function formatCount(n) {
  const value = Number(n)
  if (!Number.isFinite(value) || value < 0) return '0'
  return value.toLocaleString()
}

/** Scale of the export for admins (orders + expanded line rows when ready). */
function countsSummary(job) {
  const status = job?.status
  const display = job?.displayStatus
  const ready = status === 'completed' || display === 'ready' || status === 'expired'
  const orderCount = Number(job?.productCount)
  const rowCount = Number(job?.barcodeCount)

  if (ready && (orderCount > 0 || rowCount > 0)) {
    const parts = [`Orders ${formatCount(orderCount)}`]
    if (rowCount > 0) parts.push(`Rows ${formatCount(rowCount)}`)
    return parts.join(' · ')
  }

  // Queued / preparing: show requested size when known (selected PO IDs).
  const requested = Number(job?.rowLimit)
  if (Number.isFinite(requested) && requested > 0) {
    return `Up to ${formatCount(requested)} order${requested === 1 ? '' : 's'}`
  }

  if (job?.downloadAll || job?.exportMode === 'date_range') {
    return status === 'queued' || status === 'processing' || display === 'preparing'
      ? 'Date-range export (count after ready)'
      : '—'
  }

  return '—'
}

const PURCHASE_ORDER_EXPORT_ORG_IDS_MARKER = '__org_ids__'

function parseOrgIdsFromExportColumns(columns) {
  if (columns[5] !== PURCHASE_ORDER_EXPORT_ORG_IDS_MARKER) return undefined
  const orgIds = columns
    .slice(6)
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)
  return orgIds.length ? orgIds : undefined
}

function retryPayloadFromJob(job) {
  const columns = Array.isArray(job.selectedColumns) ? job.selectedColumns : []
  const format = columns[1] === 'csv' ? 'csv' : job.exportFormat === 'csv' ? 'csv' : 'xlsx'
  const mode = columns[2] === 'date_range' ? 'date_range' : job.exportMode === 'date_range' ? 'date_range' : 'ids'
  if (mode === 'date_range') {
    const startDate = columns[3]
    const endDate = columns[4]
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate || '') || !/^\d{4}-\d{2}-\d{2}$/.test(endDate || '')) {
      throw new Error('Could not restore the original date range. Start a new export.')
    }
    const orgIds = parseOrgIdsFromExportColumns(columns)
    return {
      mode,
      format,
      start_date: startDate,
      end_date: endDate,
      ...(orgIds ? { org_ids: orgIds } : {}),
    }
  }
  const orderIds = columns
    .slice(3)
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)
  if (orderIds.length === 0) {
    throw new Error('Could not restore the original purchase orders. Start a new export.')
  }
  return { mode: 'ids', format, order_ids: orderIds }
}

function resolveStatusKey(status, displayStatus) {
  if (displayStatus === 'no_data') return 'no_data'
  if (status === 'processing' || displayStatus === 'preparing') return 'preparing'
  if (status === 'completed' || displayStatus === 'ready') return 'ready'
  return displayStatus || status || 'failed'
}

function isActiveExportJob(job) {
  const status = job?.status
  const display = job?.displayStatus
  return (
    status === 'queued' ||
    status === 'processing' ||
    display === 'preparing' ||
    display === 'queued'
  )
}

function StatusChip({ status, displayStatus }) {
  const key = resolveStatusKey(status, displayStatus)
  const style = STATUS_STYLES[key] || STATUS_STYLES.failed
  return (
    <Chip
      size="small"
      label={style.label}
      sx={{
        fontWeight: 600,
        bgcolor: style.bgcolor,
        color: style.color,
        border: 'none',
      }}
    />
  )
}

const PurchaseOrderExportsPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canExportOrders = canExportData(adminPermissions)
  const isCompact = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const [jobs, setJobs] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [toast, setToast] = useState(null)
  const [downloadingId, setDownloadingId] = useState(null)
  const [retryingId, setRetryingId] = useState(null)

  const hasActiveJobs = useMemo(() => jobs.some(isActiveExportJob), [jobs])

  const fetchJobs = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) setLoading(true)
        setError(null)
        const response = await adminOrderService.getPurchaseOrderExportJobs({
          page: page + 1,
          limit: rowsPerPage,
        })
        setJobs(response.items || [])
        setTotal(response.pagination?.total || 0)
      } catch (err) {
        if (!silent) {
          setError(
            err.response?.data?.message ||
              'Could not load purchase-order exports. Check your connection and try again.',
          )
        }
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [page, rowsPerPage],
  )

  useEffect(() => {
    fetchJobs()
  }, [fetchJobs])

  useEffect(() => {
    const queuedMessage = location.state?.queuedMessage
    if (!queuedMessage) return
    setToast(queuedMessage)
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.state, location.pathname, navigate])

  useEffect(() => {
    if (!hasActiveJobs) return undefined
    const id = setInterval(() => {
      fetchJobs({ silent: true })
    }, UPLOAD_HISTORY_POLL_MS)
    return () => clearInterval(id)
  }, [hasActiveJobs, fetchJobs])

  if (!canExportOrders) {
    return (
      <Navigate
        to="/orders/purchase"
        replace
        state={{
          permissionDenied:
            'You need Excel / CSV export permission to view purchase-order exports.',
        }}
      />
    )
  }

  const handleDownload = async (job) => {
    if (!job.canDownload) {
      setToast(
        job.status === 'expired'
          ? 'This file expired after 7 days. Start a new export from Purchase orders.'
          : job.displayStatus === 'no_data'
            ? job.errorMessage ||
              'No purchase orders matched this export. Adjust the date range or shop and start a new export.'
            : job.status === 'failed'
              ? job.errorMessage || 'This export failed. Start a new export from Purchase orders.'
              : 'Your file is still being prepared. Wait until status is Ready.',
      )
      return
    }

    try {
      setDownloadingId(job.id)
      const { downloadUrl, fileName } = await adminOrderService.getPurchaseOrderExportJobUrl(
        job.id,
      )
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = fileName || 'purchase-orders-export.xlsx'
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setToast('Download started.')
    } catch (err) {
      setToast(
        err.response?.data?.message ||
          'Could not download this file. Refresh and try again.',
      )
    } finally {
      setDownloadingId(null)
    }
  }

  const handleRetry = async (job) => {
    try {
      setRetryingId(job.id)
      const payload = retryPayloadFromJob(job)
      const response = await adminOrderService.exportPurchaseOrders(payload)
      setToast(
        `Export started again${response?.jobId ? ` (Job ID ${response.jobId})` : ''}. Watching for progress…`,
      )
      setPage(0)
      await fetchJobs()
    } catch (err) {
      setToast(
        err.response?.data?.message ||
          err.message ||
          'Could not restart this export. Check your connection and try again.',
      )
    } finally {
      setRetryingId(null)
    }
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/orders/purchase')}
            sx={{ mb: 1, color: '#e90000' }}
          >
            Back to Purchase orders
          </Button>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#1a1a1a',
              mb: 0.5,
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
            }}
          >
            Purchase Order Exports
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Track Excel and CSV exports. Excel files include a Shipments sheet and a
            Legend sheet; CSV files include those sections after the purchase-order
            table. Ready files stay available for 7 days.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => fetchJobs()}
          sx={{ color: '#e90000', borderColor: '#e90000' }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, wordBreak: 'break-word' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {hasActiveJobs && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Preparing your file… This page updates automatically while jobs are queued or preparing.
        </Alert>
      )}

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}
      >
        {loading && jobs.length === 0 ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : jobs.length === 0 ? (
          <Box sx={{ py: 8, px: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              No exports yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Start one from Purchase orders → Export Purchase Orders.
            </Typography>
            <Button
              component={RouterLink}
              to="/orders/purchase"
              variant="contained"
              sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#c00000' } }}
            >
              Go to Purchase orders
            </Button>
          </Box>
        ) : (
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell>Job ID</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Count</TableCell>
                <TableCell>Format</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell>
                    <Typography
                      component="code"
                      variant="caption"
                      title={job.id}
                      sx={{
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        fontSize: 12,
                        wordBreak: 'break-all',
                        display: 'block',
                        maxWidth: 160,
                      }}
                    >
                      {job.id}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDateTime(job.createdAt)}</TableCell>
                  <TableCell>
                    <StatusChip status={job.status} displayStatus={job.displayStatus} />
                    {(job.displayStatus === 'no_data' || job.status === 'failed') &&
                      job.errorMessage && (
                      <Typography
                        variant="caption"
                        color={job.displayStatus === 'no_data' ? 'text.secondary' : 'error'}
                        sx={{ display: 'block', mt: 0.5, maxWidth: 280 }}
                      >
                        {job.errorMessage}
                      </Typography>
                    )}
                    {job.status === 'expired' && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5, maxWidth: 280 }}
                      >
                        This file expired after 7 days. Start a new export from Purchase orders.
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{scopeSummary(job)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                      {countsSummary(job)}
                    </Typography>
                  </TableCell>
                  <TableCell>{job.exportFormat === 'csv' ? 'CSV' : 'Excel'}</TableCell>
                  <TableCell>{formatDateTime(job.expiresAt)}</TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                      }}
                    >
                      {job.canRetry && (
                        <Button
                          size="small"
                          startIcon={
                            retryingId === job.id ? <CircularProgress size={14} /> : <ReplayIcon />
                          }
                          onClick={() => handleRetry(job)}
                          disabled={retryingId === job.id}
                        >
                          Try again
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={
                          downloadingId === job.id ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <DownloadIcon />
                          )
                        }
                        disabled={!job.canDownload || downloadingId === job.id}
                        onClick={() => handleDownload(job)}
                        sx={{
                          bgcolor: '#e90000',
                          '&:hover': { bgcolor: '#c00000' },
                          '&.Mui-disabled': { bgcolor: '#f5c0c0', color: '#fff' },
                        }}
                      >
                        {isCompact
                          ? 'Download'
                          : `Download ${job.exportFormat === 'csv' ? 'CSV' : 'Excel'}`}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {jobs.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10) || 20)
              setPage(0)
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage={isCompact ? 'Rows' : 'Rows per page:'}
          />
        )}
      </TableContainer>

      <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
        Need a new export?{' '}
        <Link component={RouterLink} to="/orders/purchase" underline="hover">
          Open Purchase orders
        </Link>
      </Typography>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  )
}

export default PurchaseOrderExportsPage
